import { NextPage, GetStaticPropsContext } from "next";
import { getGlobals } from "@models/globals.model";
import { getProductPage, getProductPagesPaths } from "@models/product-page.model";
import { getProductGlobals, ProductGlobalSchema } from "@models/product-globals.model";
import { SanitySyncProductDocument } from "@models/product.model";
import { Sanity } from "@models/sanity.model";
import Layout from "@components/layout";
import Product from "@components/product";
import ComponentResolver from "@components/component-resolver";
import { getSectioningContext } from "@components/component-resolver/sectionContext";

interface ProductPageProps extends Sanity.PageProps {
  productDocument: SanitySyncProductDocument;
  productGlobals: ProductGlobalSchema;
}

const ProductPage: NextPage<ProductPageProps> = ({ globals, productDocument, productGlobals }) => {
  const { slug, marketing_modules, tags, seo } = productDocument;
  const isBundle = tags.includes("bundle");

  const { guarantee_modal, membership_modal, finance_options_modal } = productGlobals;

  const modals = {
    guarantee: guarantee_modal,
    membership: membership_modal,
    finance: finance_options_modal,
  };

  let productModules: any[] = [];

  // Grab correct modules depending on product type (bundle or accessory)
  if (productGlobals) {
    const { accessory_marketing_modules, bundle_marketing_modules } = productGlobals;
    productModules = isBundle ? bundle_marketing_modules : accessory_marketing_modules;
  }

  return (
    <Layout globals={globals} pageID={slug.current} seo={seo}>
      <Product productDocument={productDocument} modals={modals} />

      {/* Product Specific Modules */}
      {marketing_modules?.length > 0 &&
        marketing_modules.map((schema: Sanity.Document, i: number) => {
          const sectioningContext = getSectioningContext({ modules: marketing_modules, index: i });
          return (
            <ComponentResolver
              key={`${schema._type}_${i}`}
              schema={schema}
              sectioningContext={sectioningContext}
              {...(isBundle ? { product: productDocument } : {})}
            />
          );
        })}

      {/* Product Global Modules */}
      {productModules?.length > 0 &&
        productModules.map((schema: Sanity.Document, i: number) => {
          const sectioningContext = getSectioningContext({ modules: productModules, index: i });
          return (
            <ComponentResolver
              key={`pm_${schema._type}_${i}`}
              schema={schema}
              sectioningContext={sectioningContext}
              {...(isBundle ? { product: productDocument } : {})}
            />
          );
        })}
    </Layout>
  );
};

export default ProductPage;

export async function getStaticProps(context: GetStaticPropsContext) {
  const [globals, productDocument, productGlobals] = await Promise.all([
    await getGlobals(),
    await getProductPage(String(context.params?.slug)),
    await getProductGlobals(),
  ]);

  return {
    props: {
      globals,
      productDocument,
      productGlobals,
    },
  };
}

export async function getStaticPaths() {
  const paths = await getProductPagesPaths();
  return { paths, fallback: false };
}
